/* eslint-disable @nx/enforce-module-boundaries */
import {
  AfterViewInit,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import {
  BaseListItems,
  CardTableComponent,
  ConfirmDialogService,
  CustomPaginator,
  FilterParams,
  LoaderIndicatorDirective,
  SearchComponent,
} from '@t-llevo/common-ui';
import { TravelService } from '../../../api/services/travel.service';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { NameFormaterPipe } from 'admin-drivers/src/app/common/pipes/NameFormater.pipe';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { TravelDetailsComponent } from '../travel-details/travel-details.component';
import { MatSort, MatSortModule, SortDirection } from '@angular/material/sort';
import { MatSelectModule } from '@angular/material/select';
import { BehaviorSubject, last, map, merge, take, tap } from 'rxjs';
import { VehicleTypesService } from 'admin-drivers/src/app/api/services/vehicle-types.service';
import { DateFilterComponent } from 'admin-drivers/src/app/common/components/date-filter/date-filter.component';
import { SelectFilterComponent } from 'admin-drivers/src/app/common/components/select-filter/select-filter.component';
import { Travel } from 'admin-drivers/src/app/common/models/Travel';
import { ResponseData } from '@t-llevo/util';
import { TravelResponse } from 'admin-drivers/src/app/api/models/responses/TravelResponse';
import { apiTravelRoutes as apiRoutes } from 'admin-drivers/src/app/api/routes/api-travel-routes';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TagComponent } from 'admin-drivers/src/app/common/components/tag.component';
import { FormatTravelStatusPipe } from '../../pipes/format-travel-status.pipe';
import { TravelStatusColorPipe } from '../../pipes/travel-status-color.pipe';

@Component({
  selector: 't-llevo-travel-list',
  standalone: true,
  imports: [
    CommonModule,
    CardTableComponent,
    SearchComponent,
    MatTableModule,
    LoaderIndicatorDirective,
    MatIconModule,
    MatPaginatorModule,
    MatTabsModule,
    NameFormaterPipe,
    MatTooltipModule,
    MatMenuModule,
    MatButtonModule,
    MatNativeDateModule,
    MatDialogModule,
    MatSortModule,
    MatSelectModule,
    DateFilterComponent,
    SelectFilterComponent,
    TagComponent,
    FormatTravelStatusPipe,
    TravelStatusColorPipe,
  ],
  templateUrl: './travel-list.component.html',
  styleUrl: './travel-list.component.css',
  providers: [
    ConfirmDialogService,
    { provide: MatPaginatorIntl, useValue: CustomPaginator() }
  ],
})
export class TravelListComponent
  extends BaseListItems<Travel,
  ResponseData<TravelResponse>>
  implements OnChanges, OnDestroy, AfterViewInit
{
  @Input() searchTerm = '';
  displayedColumns: string[] = [
    'createdAt',
    'vehicleType',
    //'client',
    'driver',
    //'price',
    'status',
    'actions',
  ];
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('statusFilter') statusFilter!: SelectFilterComponent;
  @ViewChild('vehicleTypeFilter') vehicleTypeFilter!: SelectFilterComponent;
  @ViewChild('searchFilter') searchFilter!: SearchComponent
  @ViewChild('dateFilter') dateFilter!: DateFilterComponent

  showDrawer = false;
  sortDirection: SortDirection = 'asc'
  destroyRef = inject(DestroyRef);
  private travelOrderSubject = new BehaviorSubject<string>('new_travels')
  travelOrder$ = this.travelOrderSubject.asObservable()

  protected vehicleTypeSrv = inject(VehicleTypesService)
  
  VehicleTypesNames$ = this.vehicleTypeSrv.getVehicleTypes()
  .pipe(map(data => data.map(item => {
    return {
      id: item.id,
      value: item.name
    }
  })))

  TravelStates = [
    {
      id: 'CANCELLED',
      value: 'Cancelado'
    },
    {
      id: 'COLLECTED',
      value: 'En recogida'
    },
    {
      id: 'EXECUTED',
      value: 'En ejecución'
    },
    {
      id: 'FINALIZED',
      value: 'Finalizado'
    },
    {
      id: 'REQUESTED',
      value: 'En solicitud'
    },
  ]

  constructor(public dialog: MatDialog, private travelSrv: TravelService) {
    super(travelSrv, apiRoutes.routeTravel);
    this.vehicleTypeSrv.getVehicleTypes().pipe(take(1)).subscribe()
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchTerm']) {
      this.onSearch(changes['searchTerm'].currentValue);
    }
  }
  ngOnDestroy(): void {
    this.stop$.next(true);
    this.stop$.complete();
  }
  ngAfterViewInit(): void {
    this.sort.sortChange
      .pipe(takeUntilDestroyed(this.destroyRef), last())
      .subscribe(() => (this.paginator.pageIndex = 0));
    merge(this.paginator.page, this.sort.sortChange)
      .pipe(tap(() => this.loadItems(this._getFiltersParams())))
      .subscribe();
    this.loadItems(this._getFiltersParams());
  }

  _getFiltersParams(): FilterParams {
    const params: FilterParams = {
      page: this.paginator ? this.paginator.pageIndex + 1 : 1,
      limit: this.paginator ? this.paginator.pageSize : 10,
    };
    if (this.searchParam) {
      params['search'] = this.searchParam;
    }

    return params;
  }

  override onSearch(ev: string): void {
    if (this.searchParam !== ev || this.searchParam === '') {
      this.searchParam = ev;
    }
  }

  detail(item: Travel): void {
    this.showDrawer = !this.showDrawer;
    this.selectItem(item.id);
  }

  openedDrawerChange(open: boolean) {
    this.showDrawer = open;
  }

  private selectItem(id: number | string) {
    this.travelSrv.getItem('', id, false);
  }

  openDetailsDialog(item: Travel) {
    this.dialog.open(TravelDetailsComponent, {
      data: item,
      height: '70%',
      width: '90%',
      disableClose: true,
      panelClass: ['m-auto'],
    });
  }

  filter(){
    let filteredData: Travel[] = []
    this.dataSource.connect().subscribe((data) => {
      if(this.statusFilter.selectedItems.length > 0){  
        filteredData = data.filter(item => this.statusFilter.selectedItems.includes(item.status))
      }
      else{
        filteredData = data.slice(0, data.length)
      }
    });

    if(this.vehicleTypeFilter.selectedItems.length > 0){
      filteredData = filteredData.filter(item => {
        if(item.vehicleType){
          return this.vehicleTypeFilter.selectedItems.includes(item.vehicleType.id)
        }
        return false
      })
    }

    if(this.dateFilter.value){
      if(this.dateFilter.selectedFilter == 'range'){
        const filterValue = this.dateFilter.value as {end: Date, start: Date}
        const endDate = new Date(filterValue.end)
        endDate.setDate(filterValue.end.getDate() + 1)
        filteredData = filteredData.filter(item => 
          new Date(item.createdAt) <= endDate && 
          new Date(item.createdAt) >= filterValue.start)
      }
      else if(this.dateFilter.selectedFilter == 'before'){
        const filterValue = this.dateFilter.value as Date
        filteredData = filteredData.filter(item => {
          return new Date(item.createdAt) <= filterValue
        })
      }
      else if(this.dateFilter.selectedFilter == 'after'){
        const filterValue = this.dateFilter.value as Date
        filteredData = filteredData.filter(item => {
          return new Date(item.createdAt) >= filterValue
        })
      }
    }
      
    const search = this.searchFilter.searchControl.value?.toLowerCase()
    if(search){
      filteredData = filteredData.filter(item => {
        if (
          item.driver?.user.firstname?.toLowerCase().includes(search) ||
          item.driver?.user.lastname?.toLowerCase().includes(search) ||
          item.vehicleType?.name.toLowerCase().includes(search) ||
          item.status.toLowerCase().includes(search)
        ) {
          return true
        }
        return false
      })
    }

    if(this.sortDirection == 'asc'){
      filteredData = filteredData.reverse()
    }
  }
}
